import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  username: string;
  email: string;
  avatar: string;
  role: 'admin' | 'author';
  authorId?: string;
  createdAt: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (username: string, email: string, password: string, avatar: string, authorId?: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEYS = {
  TOKEN: 'admin_token',
  USER: 'admin_user',
  USERS: 'admin_users_list',
};

// 初始化虚拟作者账号
const initializeDefaultUsers = () => {
  const existingUsers = localStorage.getItem(STORAGE_KEYS.USERS);
  if (!existingUsers) {
    const defaultUsers: User[] = [
      {
        id: '1',
        username: '易无忧',
        email: 'admin@example.com',
        avatar: 'https://public.readdy.ai/ai/img_res/69f0359c-399a-4e51-9b37-4470997fb35b.png',
        role: 'admin',
        authorId: 'yiwuyou',
        createdAt: new Date().toISOString(),
      },
    ];
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(defaultUsers));
    // 存储默认密码映射（实际项目中应该加密）
    localStorage.setItem('admin_passwords', JSON.stringify({
      'admin@example.com': 'admin123',
    }));
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    initializeDefaultUsers();
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const savedUser = localStorage.getItem(STORAGE_KEYS.USER);
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    const usersData = localStorage.getItem(STORAGE_KEYS.USERS);
    const passwordsData = localStorage.getItem('admin_passwords');
    
    if (!usersData || !passwordsData) return false;

    const users: User[] = JSON.parse(usersData);
    const passwords: Record<string, string> = JSON.parse(passwordsData);

    const foundUser = users.find(u => u.email === email);
    if (foundUser && passwords[email] === password) {
      const token = `token_${Date.now()}_${Math.random()}`;
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(foundUser));
      setUser(foundUser);
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const register = async (
    username: string,
    email: string,
    password: string,
    avatar: string,
    authorId?: string
  ): Promise<boolean> => {
    const usersData = localStorage.getItem(STORAGE_KEYS.USERS);
    const passwordsData = localStorage.getItem('admin_passwords');
    
    if (!usersData || !passwordsData) return false;

    const users: User[] = JSON.parse(usersData);
    const passwords: Record<string, string> = JSON.parse(passwordsData);

    // 检查邮箱是否已存在
    if (users.some(u => u.email === email)) {
      return false;
    }

    const newUser: User = {
      id: `user_${Date.now()}`,
      username,
      email,
      avatar,
      role: 'author',
      authorId,
      createdAt: new Date().toISOString(),
    };

    users.push(newUser);
    passwords[email] = password;

    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(users));
    localStorage.setItem('admin_passwords', JSON.stringify(passwords));

    return true;
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    setUser(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}